import React, { useState, useEffect } from 'react';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import DPAModal from '../components/DPAModal';
import OPProfile from './OPProfile';
import PremiumDashboardOP from './PremiumDashboardOP';
import LPEditorV4 from './LPEditorV4';
import PremiumLayout from '../components/layout/PremiumLayout';
import ApprovalList from '../components/ApprovalList';
import AmbassadorsList from './AmbassadorsList';
import ReassignLeadModal from '../components/ReassignLeadModal';

import QRModal from '../components/QRModal';

// Componenta principală OP Dashboard
export default function OPDashboard() {
  const { user } = useAuth();
  const location = useLocation();

  // DPA Check
  const showDPA = user && user.role === 'op' && !user.acceptatDPA;

  if (showDPA) {
    return <DPAModal isOpen={true} onAccept={() => window.location.reload()} />;
  }

  // Restricție rute: CSO și Rețea nu pot accesa rute operaționale (doar Dashboard, Structură, Profil)
  const isOperationalRoute = ['/op/landing-pages', '/op/approvals', '/op/leads', '/op/ambassadors', '/op/linkuri'].includes(location.pathname) || /^\/op\/editor-lp(\/\d+)?$/.test(location.pathname);
  if ((user?.role === 'cso' || user?.role === 'retea') && isOperationalRoute) {
    return <Navigate to="/op" replace />;
  }

  // Dashboard Premium (singura interfață vizibilă) - /op și /op/dashboard-analytics
  if (location.pathname === '/op' || location.pathname === '/op/' || location.pathname === '/op/dashboard-analytics') {
    return <PremiumDashboardOP />;
  }

  // Pagina Structură (subalterni direcți: Rețele/Regiuni/Manageri/Agenți)
  if (location.pathname === '/op/structura') {
    return <StructuraContent />;
  }

  // Pagina Agenți (legacy - redirect la structură pentru consistență)
  if (location.pathname === '/op/agenti') {
    return <Navigate to="/op/structura" replace />;
  }

  // Pagina Lead-uri - PremiumLayout
  if (location.pathname === '/op/leads') {
    return <LeadsListContent />;
  }

  // Pagina Ambasadori - PremiumLayout
  if (location.pathname === '/op/ambassadors') {
    return <AmbassadorsList />;
  }

  // Pagina Landing Pages - PremiumLayout
  if (location.pathname === '/op/landing-pages') {
    return <LandingPagesListContent />;
  }

  // Editor Landing Page V4 - full-screen premium
  if (location.pathname.match(/^\/op\/editor-lp(\/\d+)?$/)) {
    return <LPEditorV4 />;
  }

  // Pagina Aprobări LP - PremiumLayout
  if (location.pathname === '/op/approvals') {
    return (
      <PremiumLayout title="Aprobări LP" subtitle="Gestionează cererile agenților" variant="op" topActions={null}>
        <ApprovalList />
      </PremiumLayout>
    );
  }

  // Pagina Link-uri Generate - PremiumLayout
  if (location.pathname === '/op/linkuri') {
    return <LinksList />;
  }

  // Pagina Profil - PremiumLayout
  if (location.pathname === '/op/profile') {
    return <OPProfile />;
  }

  // Rute necunoscute -> redirect la Dashboard Premium
  return <Navigate to="/op" replace />;
}

// ============================================
// AGENȚI LIST (PremiumLayout - Echipa Ta) – păstrat pentru compatibilitate, /op/agenti redirect la structură
// ============================================
// eslint-disable-next-line no-unused-vars
function AgentiListContent() {
  const [agenti, setAgenti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);

  useEffect(() => {
    fetchAgenti();
  }, []);

  const fetchAgenti = async () => {
    try {
      const response = await api.get('/op/agenti');
      setAgenti(response.data.agents || []);
    } catch (error) {
      console.error('Eroare încărcare agenți:', error);
    } finally {
      setLoading(false);
    }
  };

  const topActions = (
    <button
      type="button"
      onClick={() => setShowAddModal(true)}
      className="bg-blue-600 text-white rounded-md px-4 py-2 font-medium hover:bg-blue-700 transition-colors flex items-center gap-2"
    >
      <span>➕</span> Adaugă Agent Nou
    </button>
  );

  if (loading) {
    return (
      <PremiumLayout title="Echipa Ta" subtitle="Gestionează agenții și datele lor" variant="op" topActions={null}>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-blue" />
        </div>
      </PremiumLayout>
    );
  }

  return (
    <PremiumLayout title="Echipa Ta" subtitle="Gestionează agenții și datele lor" variant="op" topActions={topActions}>
    <div className="space-y-6">
      <div className="section-header" style={{ marginBottom: 0 }}>
        <span className="section-title">Agenți</span>
        <span className="section-line" />
      </div>

      {agenti.length === 0 ? (
        <div className="agent-leads-table-wrap p-12 text-center">
          <div className="text-6xl mb-4 opacity-50">👤</div>
          <p className="text-[var(--text)] font-medium mb-2">Nu ai agenți adăugați încă.</p>
          <p className="text-sm text-[var(--text-muted)] mb-6 max-w-sm mx-auto">
            Adaugă agenți pentru a le putea atribui lead-uri și a genera link-uri personalizate.
          </p>
          <button
            onClick={() => setShowAddModal(true)}
            className="px-4 py-2 rounded-lg font-medium transition-colors bg-[var(--accent-glow)] text-[#8ba4fc] border border-[rgba(79,110,247,0.2)] hover:opacity-90"
          >
            Adaugă primul agent
          </button>
        </div>
      ) : (
        <div className="agent-leads-table-wrap">
          <div className="overflow-x-auto">
            <table className="agent-leads-table">
              <thead>
                <tr>
                  <th>Agent</th>
                  <th>Contact</th>
                  <th>Status</th>
                  <th>Performanță</th>
                  <th>Acțiuni</th>
                </tr>
              </thead>
              <tbody>
                {agenti.map((agent) => (
                  <tr key={agent.id}>
                    <td>
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 flex-shrink-0 rounded-full bg-purple-500/20 flex items-center justify-center text-purple-400 font-bold border border-purple-500/30">
                          {agent.numeComplet ? agent.numeComplet.charAt(0).toUpperCase() : 'A'}
                        </div>
                        <span className="font-medium text-[var(--text)]">{agent.numeComplet}</span>
                      </div>
                    </td>
                    <td>
                      <div className="text-[var(--text)]">{agent.telefon}</div>
                      <div className="text-xs text-[var(--text-muted)]">{agent.email}</div>
                    </td>
                    <td>
                      <span className={`px-3 py-1 text-xs font-bold rounded-full uppercase tracking-wide ${agent.status === 'activ'
                        ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30'
                        : 'bg-amber-500/15 text-amber-400 border border-amber-500/30'
                        }`}>
                        {agent.status}
                      </span>
                    </td>
                    <td>
                      <span className="font-semibold text-[var(--text)]">{agent._count?.leads || 0}</span>
                      <span className="text-xs text-[var(--text-muted)] ml-1">lead-uri</span>
                    </td>
                    <td>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => { setSelectedAgent(agent); setShowEditModal(true); }}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors bg-blue-500/15 text-blue-400 hover:bg-blue-500/25 border border-blue-500/30"
                          title="Editează"
                        >
                          <span>✎</span> Editează
                        </button>
                        <button
                          onClick={() => { setSelectedAgent(agent); setShowDeleteModal(true); }}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors bg-red-500/15 text-red-400 hover:bg-red-500/25 border border-red-500/30"
                          title="Șterge"
                        >
                          <span>🗑</span> Șterge
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showAddModal && <AddAgentModal onClose={() => setShowAddModal(false)} onSuccess={fetchAgenti} />}
      {showEditModal && selectedAgent && (
        <EditAgentModal
          agent={selectedAgent}
          onClose={() => { setShowEditModal(false); setSelectedAgent(null); }}
          onSuccess={() => { fetchAgenti(); setShowEditModal(false); setSelectedAgent(null); }}
        />
      )}
      {showDeleteModal && selectedAgent && (
        <DeleteAgentModal
          agent={selectedAgent}
          onClose={() => { setShowDeleteModal(false); setSelectedAgent(null); }}
          onSuccess={() => { fetchAgenti(); setShowDeleteModal(false); setSelectedAgent(null); }}
        />
      )}
    </div>
    </PremiumLayout>
  );
}

// ============================================
// STRUCTURĂ (Subalterni direcți: Rețele / Regiuni / Manageri / Agenți)
// ============================================

function StructuraContent() {
  const { user } = useAuth();
  const [type, setType] = useState('agenti');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const labels = {
    cso: { title: 'Rețele', subtitle: 'Rețelele din structura ta', add: 'Adaugă Rețea' },
    retea: { title: 'Regiuni', subtitle: 'Regiunile din structura ta', add: 'Adaugă Regiune' },
    regiune: { title: 'Manageri', subtitle: 'Managerii din structura ta', add: 'Adaugă Manager' },
    manager: { title: 'Agenți', subtitle: 'Agenții din echipa ta', add: 'Adaugă Agent' },
    op: { title: 'Agenți', subtitle: 'Agenții din echipa ta', add: 'Adaugă Agent' },
  };
  const role = (user?.role || 'op').toLowerCase();
  const { title, subtitle, add } = labels[role] || labels.op;

  const fetchSubordinates = async () => {
    try {
      const res = await api.get('/op/subordinates');
      if (res.data.success) {
        setType(res.data.type || 'agenti');
        setItems(res.data.items || []);
      }
    } catch (err) {
      console.error('Eroare încărcare subalterni:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSubordinates(); }, []);

  const topActions = (
    <button
      type="button"
      onClick={() => setShowAddModal(true)}
      className="bg-blue-600 text-white rounded-md px-4 py-2 font-medium hover:bg-blue-700 transition-colors flex items-center gap-2"
    >
      <span>➕</span> {add}
    </button>
  );

  if (loading) {
    return (
      <PremiumLayout title={title} subtitle={subtitle} variant="op" topActions={null}>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-blue" />
        </div>
      </PremiumLayout>
    );
  }

  return (
    <PremiumLayout title={title} subtitle={subtitle} variant="op" topActions={topActions}>
      <div className="space-y-6">
        <div className="section-header" style={{ marginBottom: 0 }}>
          <span className="section-title">{title}</span>
          <span className="section-line" />
        </div>

        {items.length === 0 ? (
          <div className="agent-leads-table-wrap p-12 text-center">
            <div className="text-6xl mb-4 opacity-50">🏢</div>
            <p className="text-[var(--text)] font-medium mb-2">Nu ai subalterni adăugați încă.</p>
            <p className="text-sm text-[var(--text-muted)] mb-6 max-w-sm mx-auto">
              Adaugă primul subaltern pentru a construi structura.
            </p>
            <button
              onClick={() => setShowAddModal(true)}
              className="px-4 py-2 rounded-lg font-medium transition-colors bg-[var(--accent-glow)] text-[#8ba4fc] border border-[rgba(79,110,247,0.2)] hover:opacity-90"
            >
              {add}
            </button>
          </div>
        ) : type === 'ops' ? (
          <div className="agent-leads-table-wrap">
            <div className="overflow-x-auto">
              <table className="agent-leads-table">
                <thead>
                  <tr>
                    <th>Nume</th>
                    <th>Username / Email</th>
                    <th>Rol</th>
                    <th>Agenți</th>
                    <th>Lead-uri</th>
                    <th className="text-right">Acțiuni</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((op) => (
                    <tr key={op.id}>
                      <td><span className="font-medium text-[var(--text)]">{op.numeComplet}</span></td>
                      <td>
                        <div className="text-[var(--text)]">{op.username}</div>
                        <div className="text-xs text-[var(--text-muted)]">{op.email}</div>
                      </td>
                      <td><span className="px-2 py-1 text-xs rounded bg-gray-500/20 text-[var(--text)]">{op.role || 'op'}</span></td>
                      <td>{op._count?.agenti ?? 0}</td>
                      <td>{op._count?.leads ?? 0}</td>
                      <td className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button type="button" onClick={() => { setSelectedItem(op); setShowEditModal(true); }} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors bg-blue-500/15 text-blue-400 hover:bg-blue-500/25 border border-blue-500/30" title="Editează">✎ Editează</button>
                          <button type="button" onClick={() => { setSelectedItem(op); setShowDeleteConfirm(true); }} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors bg-red-500/15 text-red-400 hover:bg-red-500/25 border border-red-500/30" title="Șterge">🗑 Șterge</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="agent-leads-table-wrap">
            <div className="overflow-x-auto">
              <table className="agent-leads-table">
                <thead>
                  <tr>
                    <th>Agent</th>
                    <th>Contact</th>
                    <th>Status</th>
                    <th>Performanță</th>
                    <th className="text-right">Acțiuni</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((agent) => (
                    <tr key={agent.id}>
                      <td>
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 flex-shrink-0 rounded-full bg-purple-500/20 flex items-center justify-center text-purple-400 font-bold border border-purple-500/30">
                            {agent.numeComplet ? agent.numeComplet.charAt(0).toUpperCase() : 'A'}
                          </div>
                          <span className="font-medium text-[var(--text)]">{agent.numeComplet}</span>
                        </div>
                      </td>
                      <td>
                        <div className="text-[var(--text)]">{agent.telefon}</div>
                        <div className="text-xs text-[var(--text-muted)]">{agent.email}</div>
                      </td>
                      <td>
                        <span className={`px-3 py-1 text-xs font-bold rounded-full uppercase ${agent.status === 'activ' ? 'bg-emerald-500/15 text-emerald-400' : 'bg-amber-500/15 text-amber-400'}`}>
                          {agent.status}
                        </span>
                      </td>
                      <td><span className="font-semibold">{agent._count?.leads || 0}</span> lead-uri</td>
                      <td className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button type="button" onClick={() => { setSelectedItem(agent); setShowEditModal(true); }} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors bg-blue-500/15 text-blue-400 hover:bg-blue-500/25 border border-blue-500/30" title="Editează">✎ Editează</button>
                          <button type="button" onClick={() => { setSelectedItem(agent); setShowDeleteConfirm(true); }} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors bg-red-500/15 text-red-400 hover:bg-red-500/25 border border-red-500/30" title="Șterge">🗑 Șterge</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {showAddModal && (
          <AddSubordinateModal
            role={role}
            onClose={() => setShowAddModal(false)}
            onSuccess={() => { setShowAddModal(false); fetchSubordinates(); }}
          />
        )}
        {showEditModal && selectedItem && (
          <EditSubordinateModal
            role={role}
            type={type}
            item={selectedItem}
            onClose={() => { setShowEditModal(false); setSelectedItem(null); }}
            onSuccess={() => { setShowEditModal(false); setSelectedItem(null); fetchSubordinates(); }}
          />
        )}
        {showDeleteConfirm && selectedItem && (
          <DeleteSubordinateConfirm
            type={type}
            item={selectedItem}
            onClose={() => { setShowDeleteConfirm(false); setSelectedItem(null); }}
            onConfirm={() => { setShowDeleteConfirm(false); setSelectedItem(null); fetchSubordinates(); }}
          />
        )}
      </div>
    </PremiumLayout>
  );
}

// Modal creare subaltern (Nume, Username, Email; pentru agent + Telefon)
function AddSubordinateModal({ role, onClose, onSuccess }) {
  const [numeComplet, setNumeComplet] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [telefon, setTelefon] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const isAgent = role === 'manager' || role === 'op';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await api.post('/op/subordinates', isAgent ? { numeComplet, username, email, telefon: telefon || '0000000000' } : { numeComplet, username, email });
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.error || 'Eroare la creare');
    } finally {
      setLoading(false);
    }
  };

  const addLabel = role === 'cso' ? 'Rețea' : role === 'retea' ? 'Regiune' : role === 'regiune' ? 'Manager' : 'Agent';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl max-w-md w-full p-6 border border-gray-200 dark:border-gray-700" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-semibold text-[var(--text)] mb-4">Adaugă {addLabel}</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[var(--text-muted)] mb-1">Nume complet</label>
            <input type="text" value={numeComplet} onChange={(e) => setNumeComplet(e.target.value)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-[var(--text)]" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-[var(--text-muted)] mb-1">Username</label>
            <input type="text" value={username} onChange={(e) => setUsername(e.target.value)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-[var(--text)]" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-[var(--text-muted)] mb-1">Email</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-[var(--text)]" required />
          </div>
          {isAgent && (
            <div>
              <label className="block text-sm font-medium text-[var(--text-muted)] mb-1">Telefon</label>
              <input type="tel" value={telefon} onChange={(e) => setTelefon(e.target.value)} placeholder="07xxxxxxxx" className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-[var(--text)]" />
            </div>
          )}
          {error && <p className="text-sm text-red-500">{error}</p>}
          <div className="flex gap-2 justify-end pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-[var(--text)]">Anulare</button>
            <button type="submit" disabled={loading} className="px-4 py-2 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 disabled:opacity-50">Salvează</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Modal editare subaltern (pre-completat; parolă opțională)
function EditSubordinateModal({ role, type, item, onClose, onSuccess }) {
  const isOp = type === 'ops';
  const [numeComplet, setNumeComplet] = useState(item?.numeComplet || '');
  const [username, setUsername] = useState(item?.username || '');
  const [email, setEmail] = useState(item?.email || '');
  const [password, setPassword] = useState('');
  const [telefon, setTelefon] = useState(item?.telefon || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setNumeComplet(item?.numeComplet || '');
    setUsername(item?.username || '');
    setEmail(item?.email || '');
    setTelefon(item?.telefon || '');
  }, [item]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const payload = isOp ? { numeComplet, username, email } : { numeComplet, email, telefon };
      if (password && password.length >= 8) payload.password = password;
      await api.put(`/op/structure/${item.id}`, payload);
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.error || 'Eroare la actualizare');
    } finally {
      setLoading(false);
    }
  };

  const editLabel = role === 'cso' ? 'Rețea' : role === 'retea' ? 'Regiune' : role === 'regiune' ? 'Manager' : 'Agent';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl max-w-md w-full p-6 border border-gray-200 dark:border-gray-700" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-semibold text-[var(--text)] mb-4">Editează {editLabel}</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[var(--text-muted)] mb-1">Nume complet</label>
            <input type="text" value={numeComplet} onChange={(e) => setNumeComplet(e.target.value)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-[var(--text)]" required />
          </div>
          {isOp && (
            <div>
              <label className="block text-sm font-medium text-[var(--text-muted)] mb-1">Username</label>
              <input type="text" value={username} onChange={(e) => setUsername(e.target.value)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-[var(--text)]" required />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-[var(--text-muted)] mb-1">Email</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-[var(--text)]" required />
          </div>
          {!isOp && (
            <div>
              <label className="block text-sm font-medium text-[var(--text-muted)] mb-1">Telefon</label>
              <input type="tel" value={telefon} onChange={(e) => setTelefon(e.target.value)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-[var(--text)]" />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-[var(--text-muted)] mb-1">Parolă nouă (opțional, min. 8 caractere)</label>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Lăsați gol pentru a păstra parola" className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-[var(--text)]" minLength={8} />
          </div>
          {error && <p className="text-sm text-red-500">{error}</p>}
          <div className="flex gap-2 justify-end pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-[var(--text)]">Anulare</button>
            <button type="submit" disabled={loading} className="px-4 py-2 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 disabled:opacity-50">Salvează</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Confirmare ștergere subaltern
function DeleteSubordinateConfirm({ type, item, onClose, onConfirm }) {
  const [loading, setLoading] = useState(false);

  const handleDelete = async () => {
    setLoading(true);
    try {
      await api.delete(`/op/structure/${item.id}`);
      onConfirm();
    } catch (err) {
      console.error('Eroare ștergere:', err);
      alert(err.response?.data?.error || 'Eroare la ștergere');
    } finally {
      setLoading(false);
    }
  };

  const name = item?.numeComplet || item?.username || `#${item?.id}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl max-w-sm w-full p-6 border border-gray-200 dark:border-gray-700" onClick={(e) => e.stopPropagation()}>
        <p className="text-[var(--text)] font-medium mb-4">Ești sigur că vrei să ștergi acest cont? ({name})</p>
        <div className="flex gap-2 justify-end">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-[var(--text)]">Nu</button>
          <button type="button" onClick={handleDelete} disabled={loading} className="px-4 py-2 rounded-lg bg-red-600 text-white font-medium hover:bg-red-700 disabled:opacity-50">Da, șterge</button>
        </div>
      </div>
    </div>
  );
}

// ============================================
// EDIT AGENT MODAL
// ============================================

function EditAgentModal({ agent, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    numeComplet: agent?.numeComplet || '',
    telefon: agent?.telefon || '',
    email: agent?.email || '',
    status: agent?.status || 'activ',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (agent) {
      setFormData({
        numeComplet: agent.numeComplet || '',
        telefon: agent.telefon || '',
        email: agent.email || '',
        status: agent.status || 'activ',
      });
    }
  }, [agent]);

  const validateForm = () => {
    if (formData.numeComplet.trim().length < 2) return 'Numele trebuie să aibă minim 2 caractere.';
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) return 'Email invalid.';
    if (!/^(\+40|0)7\d{8}$/.test((formData.telefon || '').replace(/\s/g, ''))) return 'Număr de telefon invalid (format: 07xxxxxxxx).';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }
    setLoading(true);
    try {
      await api.put(`/op/agenti/${agent.id}`, {
        numeComplet: formData.numeComplet,
        telefon: formData.telefon,
        email: formData.email || null,
        status: formData.status,
      });
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.error || 'Eroare la actualizare');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100]">
      <div className="bg-white dark:bg-[#1a1d27] rounded-xl p-6 max-w-md w-full shadow-xl border border-gray-200 dark:border-white/10">
        <h3 className="text-lg font-bold mb-4 text-gray-900 dark:text-white">Editează Agent</h3>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nume complet</label>
            <input
              type="text"
              required
              value={formData.numeComplet}
              onChange={(e) => setFormData({ ...formData, numeComplet: e.target.value })}
              className="w-full border border-gray-300 dark:border-white/20 rounded-lg px-3 py-2 bg-white dark:bg-white/5 text-gray-900 dark:text-white"
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Telefon</label>
            <input
              type="tel"
              required
              value={formData.telefon}
              onChange={(e) => setFormData({ ...formData, telefon: e.target.value })}
              className="w-full border border-gray-300 dark:border-white/20 rounded-lg px-3 py-2 bg-white dark:bg-white/5 text-gray-900 dark:text-white"
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full border border-gray-300 dark:border-white/20 rounded-lg px-3 py-2 bg-white dark:bg-white/5 text-gray-900 dark:text-white"
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
            <select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              className="w-full border border-gray-300 dark:border-white/20 rounded-lg px-3 py-2 bg-white dark:bg-white/5 text-gray-900 dark:text-white"
            >
              <option value="activ">Activ</option>
              <option value="inactiv">Inactiv</option>
            </select>
          </div>
          {error && (
            <div className="mb-4 bg-red-500/15 border border-red-500/30 text-red-400 px-4 py-2 rounded-lg text-sm">
              {error}
            </div>
          )}
          <div className="flex gap-3 justify-end">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg bg-gray-200 dark:bg-white/10 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-white/15" disabled={loading}>
              Anulează
            </button>
            <button type="submit" disabled={loading} className="px-4 py-2 rounded-lg bg-brand-blue text-white hover:bg-brand-blue-hover disabled:opacity-50">
              {loading ? 'Se salvează...' : 'Salvează'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================
// DELETE AGENT MODAL
// ============================================

function DeleteAgentModal({ agent, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleDelete = async () => {
    setLoading(true);
    setError('');

    try {
      await api.delete(`/op/agenti/${agent.id}`);
      onSuccess();
    } catch (err) {
      console.error('Error deleting agent:', err);
      setError(err.response?.data?.error || 'Eroare la ștergerea agentului');
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100]">
      <div className="bg-white dark:bg-[#1a1d27] rounded-xl p-6 max-w-md w-full shadow-xl border border-gray-200 dark:border-white/10">
        <h3 className="text-lg font-bold mb-2 text-red-500 dark:text-red-400">Șterge Agent</h3>
        <p className="text-gray-600 dark:text-gray-300 mb-4">
          Ești sigur că vrei să ștergi agentul <strong>{agent.numeComplet}</strong>?
        </p>
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <span className="text-yellow-400">⚠️</span>
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-700">
                Această acțiune este ireversibilă. Agentul va fi notificat prin email, iar toate datele asociate vor fi eliminate.
              </p>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-600 px-4 py-2 rounded-lg text-sm">
            {error}
          </div>
        )}

        <div className="flex gap-3 justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            disabled={loading}
          >
            Anulează
          </button>
          <button
            onClick={handleDelete}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2"
            disabled={loading}
          >
            {loading ? 'Se șterge...' : 'Confirmă Ștergerea'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================
// ADD AGENT MODAL
// ============================================

function AddAgentModal({ onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    numeComplet: '',
    telefon: '',
    email: '',
    afiseazaNumeAgent: true,
    afiseazaTelefonAgent: true,
    afiseazaEmailAgent: true
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const validateForm = () => {
    if (formData.numeComplet.trim().length < 2) return 'Numele trebuie să aibă minim 2 caractere.';
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) return 'Email invalid.';
    // Simple RO mobile phone validation: 07xxxxxxxx or +407xxxxxxxx
    if (!/^(\+40|0)7\d{8}$/.test(formData.telefon.replace(/\s/g, ''))) return 'Număr de telefon invalid (format: 07xxxxxxxx).';
    if (formData.password && formData.password.length < 8) return 'Parola trebuie să aibă minim 8 caractere.';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);

    try {
      await api.post('/op/agenti', formData);
      onSuccess();
      onClose();
    } catch (err) {
      const apiError = err.response?.data?.error;
      const details = err.response?.data?.details;

      if (details && Array.isArray(details)) {
        setError(details.map(d => d.msg).join(', '));
      } else {
        setError(apiError || 'Eroare la crearea agentului');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100]">
      <div className="bg-white dark:bg-[#1a1d27] rounded-xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto shadow-xl border border-gray-200 dark:border-white/10">
        <h3 className="text-lg font-bold mb-4 text-gray-900 dark:text-white">Adaugă Agent Nou</h3>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nume complet
            </label>
            <input
              type="text"
              required
              value={formData.numeComplet}
              onChange={(e) => setFormData({ ...formData, numeComplet: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Telefon
            </label>
            <input
              type="tel"
              required
              value={formData.telefon}
              onChange={(e) => setFormData({ ...formData, telefon: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Parolă Inițială
            </label>
            <input
              type="password"
              value={formData.password || ''}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              placeholder="Minim 8 caractere"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Afișare pe Landing Page:
            </label>
            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.afiseazaNumeAgent}
                  onChange={(e) => setFormData({ ...formData, afiseazaNumeAgent: e.target.checked })}
                  className="mr-2"
                />
                Afișează nume agent
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.afiseazaTelefonAgent}
                  onChange={(e) => setFormData({ ...formData, afiseazaTelefonAgent: e.target.checked })}
                  className="mr-2"
                />
                Afișează telefon agent
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.afiseazaEmailAgent}
                  onChange={(e) => setFormData({ ...formData, afiseazaEmailAgent: e.target.checked })}
                  className="mr-2"
                />
                Afișează email agent
              </label>
            </div>
          </div>

          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-600 px-4 py-2 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300"
            >
              Anulează
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-brand-blue text-white px-4 py-2 rounded-lg hover:bg-brand-blue-hover disabled:opacity-50"
            >
              {loading ? 'Se adaugă...' : 'Adaugă Agent'}
            </button>
          </div>
        </form>
      </div >
    </div >
  );
}

// ============================================
// LANDING PAGES (Placeholder)
// ============================================

// ============================================
// LANDING PAGES LIST (PremiumLayout)
// ============================================

function LandingPagesListContent() {
  const navigate = useNavigate();
  const [landingPages, setLandingPages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLandingPages();
  }, []);

  const fetchLandingPages = async () => {
    try {
      const response = await api.get('/op/landing-pages');
      setLandingPages(response.data.pages || []);
    } catch (error) {
      console.error('Eroare încărcare landing pages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLandingPage = async (id) => {
    if (!window.confirm('Ești sigur că vrei să ștergi acest Landing Page? Acțiunea este ireversibilă.')) return;
    try {
      await api.delete(`/op/landing-pages/${id}`);
      setLandingPages(prev => prev.filter(p => p.id !== id));
    } catch (error) {
      console.error('Eroare ștergere LP:', error);
      alert('Eroare la ștergerea paginii.');
    }
  };

  const topActions = (
    <button
      type="button"
      onClick={() => navigate('/op/editor-lp')}
      className="bg-blue-600 text-white rounded-md px-4 py-2 font-medium hover:bg-blue-700 transition-colors flex items-center gap-2"
    >
      <span>+</span> Adaugă Landing Page
    </button>
  );

  if (loading) {
    return (
      <PremiumLayout title="Landing Pages" subtitle="Gestionează paginile de destinație" variant="op" topActions={null}>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-blue" />
        </div>
      </PremiumLayout>
    );
  }

  return (
    <PremiumLayout title="Landing Pages" subtitle="Gestionează paginile de destinație" variant="op" topActions={topActions}>
    <div className="space-y-6">
      <div className="section-header" style={{ marginBottom: 0 }}>
        <span className="section-title">Landing Pages</span>
        <span className="section-line" />
      </div>

      {landingPages.length === 0 ? (
        <div className="agent-leads-table-wrap p-12 text-center">
          <div className="text-6xl mb-4 opacity-50">🌐</div>
          <p className="text-[var(--text)] font-medium">Nu ai creat niciun landing page.</p>
          <button
            onClick={() => navigate('/op/editor-lp')}
            className="mt-4 px-4 py-2 rounded-lg font-medium bg-[var(--accent-glow)] text-[#8ba4fc] border border-[rgba(79,110,247,0.2)] hover:opacity-90"
          >
            Creează prima pagină
          </button>
        </div>
      ) : (
        <div className="agent-leads-table-wrap">
          <div className="overflow-x-auto">
            <table className="agent-leads-table">
              <thead>
                <tr>
                  <th>Titlu & Slug</th>
                  <th>Status</th>
                  <th>Setări</th>
                  <th>Rezultate</th>
                  <th>Acțiuni</th>
                </tr>
              </thead>
              <tbody>
                {landingPages.map((lp) => (
                  <tr key={lp.id}>
                    <td>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-[var(--text)]">{lp.titlu}</span>
                        <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${lp.isDraft ? 'bg-amber-500/20 text-amber-600 dark:text-amber-400 border border-amber-500/40' : 'bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 border border-emerald-500/40'}`}>
                          {lp.isDraft ? 'Ciornă' : 'Publicat'}
                        </span>
                      </div>
                      <a href={`/lp/${lp.slug}`} target="_blank" rel="noopener noreferrer" className="text-xs text-[#8ba4fc] hover:underline font-mono">
                        /lp/{lp.slug}
                      </a>
                    </td>
                    <td>
                      <span className={`px-3 py-1 text-xs font-bold rounded-full uppercase tracking-wide ${lp.activ ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30' : 'bg-red-500/15 text-red-400 border border-red-500/30'}`}>
                        {lp.activ ? 'Activ' : 'Inactiv'}
                      </span>
                    </td>
                    <td className="text-[var(--text-muted)]">
                      {lp.smsValidare ? <span className="text-xs px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-400">📱 SMS Activ</span> : <span className="text-xs">Fără SMS</span>}
                    </td>
                    <td>
                      <span className="font-semibold text-[var(--text)]">{lp._count?.leads || 0} Leads</span>
                    </td>
                    <td>
                      <div className="flex items-center gap-2">
                        <button type="button" onClick={() => navigate(`/op/editor-lp/${lp.id}`)} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium bg-blue-500/15 text-blue-400 hover:bg-blue-500/25 border border-blue-500/30">
                          ✎ Edit
                        </button>
                        <button type="button" onClick={() => handleDeleteLandingPage(lp.id)} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium bg-red-500/15 text-red-400 hover:bg-red-500/25 border border-red-500/30">
                          🗑 Șterge
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

    </div>
    </PremiumLayout>
  );
}

// ============================================
// LEADS LIST (PremiumLayout)
// ============================================

function LeadsListContent() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [leadToReassign, setLeadToReassign] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedLeadForDetail, setSelectedLeadForDetail] = useState(null);

  useEffect(() => {
    fetchLeads();
  }, []);

  const fetchLeads = async () => {
    try {
      const response = await api.get('/op/leads');
      setLeads(response.data.leads || []);
    } catch (error) {
      console.error('Eroare încărcare lead-uri:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (leadId, newStatus) => {
    setUpdatingId(leadId);
    try {
      const response = await api.post(`/op/leads/${leadId}/validate`, { status: newStatus });
      if (response.data.success) {
        setLeads(prev => prev.map(l => l.id === leadId ? { ...l, status: newStatus } : l));
      }
    } catch (error) {
      console.error('Eroare actualizare status:', error);
      alert('Nu s-a putut actualiza statusul.');
    } finally {
      setUpdatingId(null);
    }
  };

  const exportExcel = async () => {
    try {
      const response = await api.get('/op/leads/export', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `leads-${new Date().toISOString().slice(0, 10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Eroare export:', error);
      alert('Eroare la exportul lead-urilor');
    }
  };

  const getStatusBadge = (status) => {
    const s = status || 'nou';
    const base = 'px-3 py-1 text-xs font-bold rounded-full uppercase tracking-wide';
    if (s === 'validat') return `${base} bg-emerald-500/15 text-emerald-400 border border-emerald-500/30`;
    if (s === 'invalidat') return `${base} bg-red-500/15 text-red-400 border border-red-500/30`;
    if (s === 'neinteresat') return `${base} bg-gray-500/15 text-gray-400 dark:text-gray-400 border border-gray-500/30`;
    return `${base} bg-blue-500/15 text-blue-400 border border-blue-500/30`;
  };

  const filteredLeads = leads.filter(lead => {
    if (filterStatus === 'all') return true;
    return (lead.status || 'nou') === filterStatus;
  });

  const topActions = (
    <div className="flex items-center gap-3">
      <select
        value={filterStatus}
        onChange={(e) => setFilterStatus(e.target.value)}
        className="agent-filter-select"
      >
        <option value="all">Toate Lead-urile</option>
        <option value="nou">Noi</option>
        <option value="validat">Validate</option>
        <option value="invalidat">Invalidate</option>
        <option value="neinteresat">Neinteresat</option>
      </select>
      <button
        type="button"
        onClick={exportExcel}
        className="bg-green-600 text-white rounded-md px-4 py-2 font-medium hover:bg-green-700 transition-colors flex items-center gap-2"
      >
        <span>📥</span> Export Excel
      </button>
    </div>
  );

  if (loading) {
    return (
      <PremiumLayout title="Baza de Lead-uri" subtitle="Gestionează și urmărește prospecții" variant="op" topActions={null}>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
        </div>
      </PremiumLayout>
    );
  }

  return (
    <PremiumLayout title="Baza de Lead-uri" subtitle="Gestionează și urmărește prospecții" variant="op" topActions={topActions}>
    <div className="space-y-6">
      <div className="section-header" style={{ marginBottom: 0 }}>
        <span className="section-title">Lead-uri</span>
        <span className="section-line" />
      </div>

      {filteredLeads.length === 0 ? (
        <div className="agent-leads-table-wrap p-12 text-center">
          <div className="text-6xl mb-4 opacity-50">🔍</div>
          <p className="text-[var(--text)] font-medium">Nu am găsit lead-uri pentru filtrul selectat.</p>
          <button
            onClick={() => setFilterStatus('all')}
            className="mt-4 px-4 py-2 rounded-lg font-medium bg-[var(--accent-glow)] text-[#8ba4fc] border border-[rgba(79,110,247,0.2)] hover:opacity-90"
          >
            Resetează filtrele
          </button>
        </div>
      ) : (
        <div className="agent-leads-table-wrap">
          <div className="overflow-x-auto">
            <table className="agent-leads-table">
              <thead>
                <tr>
                  <th>Contact</th>
                  <th>Sursă</th>
                  <th>Agent</th>
                  <th>Status</th>
                  <th>Acțiuni</th>
                  <th>Dată</th>
                </tr>
              </thead>
              <tbody>
                {filteredLeads.map((lead) => (
                  <tr key={lead.id}>
                    <td>
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 flex-shrink-0 rounded-full bg-blue-500/20 flex items-center justify-center text-blue-400 font-bold">
                          {lead.nume ? lead.nume.charAt(0).toUpperCase() : '?'}
                        </div>
                        <div>
                          <div className="font-medium text-[var(--text)]">{lead.nume || 'Nespecificat'}</div>
                          <div className="text-xs text-[var(--text-muted)]">{lead.email}</div>
                          <div className="text-sm font-mono text-[var(--text-soft)]">
                            {lead.telefon}
                            {lead.telefonValidat && <span title="Număr Validat" className="ml-1 text-emerald-400 cursor-help">✓</span>}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="text-[var(--text)]">
                      {lead.referredByCode ? (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 border border-emerald-500/40" title={lead.referredByName ? `Recomandat de ${lead.referredByName}` : `Cod: ${lead.referredByCode}`}>
                          Recomandare: {lead.referredByName || lead.referredByCode || 'Ambasador'}
                        </span>
                      ) : (
                        lead.landingPage?.titlu || '—'
                      )}
                    </td>
                    <td className="text-[var(--text-muted)]">
                      {lead.agent ? lead.agent.numeComplet : (lead.link?.agent?.numeComplet || <span className="text-red-400 italic">Agent Șters</span>)}
                    </td>
                    <td>
                      <span className={getStatusBadge(lead.status)}>
                        {lead.status ? lead.status : 'NOU'}
                      </span>
                    </td>
                    <td>
                      <div className="flex items-center gap-2 flex-wrap">
                        {updatingId === lead.id ? (
                          <span className="inline-flex items-center px-2 py-1 text-xs rounded-md text-[var(--text-muted)] bg-[var(--bar-bg)]">
                            <svg className="animate-spin -ml-0.5 mr-1.5 h-3.5 w-3.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                            Salvare...
                          </span>
                        ) : (
                          <>
                            {lead.status !== 'validat' && (
                              <button
                                type="button"
                                onClick={() => handleStatusChange(lead.id, 'validat')}
                                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25 border border-emerald-500/30"
                                title="Marchează ca Validat"
                              >
                                ✓ Validează
                              </button>
                            )}
                            {lead.status !== 'invalidat' && (
                              <button
                                type="button"
                                onClick={() => handleStatusChange(lead.id, 'invalidat')}
                                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium bg-red-500/15 text-red-400 hover:bg-red-500/25 border border-red-500/30"
                                title="Marchează ca Invalid"
                              >
                                ✕ Invalidează
                              </button>
                            )}
                            {(lead.status === 'validat' || lead.status === 'invalidat') && (
                              <button
                                type="button"
                                onClick={() => handleStatusChange(lead.id, 'neinteresat')}
                                className="text-[var(--text-muted)] hover:text-[var(--text)] text-xs underline px-1"
                              >
                                Neinteresat
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => { setSelectedLeadForDetail(lead); setShowDetailModal(true); }}
                              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium bg-slate-500/15 text-slate-400 hover:bg-slate-500/25 border border-slate-500/30"
                              title="Detalii lead"
                            >
                              👁 Detalii
                            </button>
                            <button
                              type="button"
                              onClick={() => { setLeadToReassign(lead); setShowReassignModal(true); }}
                              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium bg-blue-500/15 text-blue-400 hover:bg-blue-500/25 border border-blue-500/30"
                              title="Transferă la alt agent"
                            >
                              ⇄ Transfer
                            </button>
                            <button
                              type="button"
                              onClick={() => { setSelectedLead(lead); setShowDeleteModal(true); }}
                              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium bg-red-500/15 text-red-400 hover:bg-red-500/25 border border-red-500/30"
                              title="Șterge"
                            >
                              🗑 Șterge
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="text-[var(--text)]">{new Date(lead.dataCompletare).toLocaleDateString('ro-RO')}</div>
                      <div className="text-xs text-[var(--text-muted)]">{new Date(lead.dataCompletare).toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' })}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showDeleteModal && selectedLead && (
        <DeleteLeadModal
          lead={selectedLead}
          onClose={() => { setShowDeleteModal(false); setSelectedLead(null); }}
          onSuccess={() => { fetchLeads(); setShowDeleteModal(false); setSelectedLead(null); }}
        />
      )}

      {showReassignModal && leadToReassign && (
        <ReassignLeadModal
          lead={leadToReassign}
          onClose={() => { setShowReassignModal(false); setLeadToReassign(null); }}
          onSuccess={() => { fetchLeads(); setShowReassignModal(false); setLeadToReassign(null); }}
        />
      )}

      {showDetailModal && selectedLeadForDetail && (
        <LeadDetailModal
          lead={selectedLeadForDetail}
          onClose={() => { setShowDetailModal(false); setSelectedLeadForDetail(null); }}
        />
      )}
    </div>
    </PremiumLayout>
  );
}

// ============================================
// LEAD DETAIL MODAL (inclusiv câmpuri custom)
// ============================================

function LeadDetailModal({ lead, onClose }) {
  let customFieldsParsed = {};
  if (lead.customData) {
    try {
      customFieldsParsed = typeof lead.customData === 'string' ? JSON.parse(lead.customData) : lead.customData;
    } catch (_) {
      customFieldsParsed = {};
    }
  }
  const hasCustomFields = Object.keys(customFieldsParsed).length > 0;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100]">
      <div className="bg-white dark:bg-[#1a1d27] rounded-xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-xl border border-gray-200 dark:border-white/10">
        <h3 className="text-lg font-bold mb-4 text-[var(--text)]">Detalii Lead</h3>
        <div className="space-y-3 text-sm">
          <div><span className="font-semibold text-[var(--text-muted)]">Nume:</span> <span className="text-[var(--text)]">{lead.nume || '—'}</span></div>
          <div><span className="font-semibold text-[var(--text-muted)]">Email:</span> <span className="text-[var(--text)]">{lead.email || '—'}</span></div>
          <div><span className="font-semibold text-[var(--text-muted)]">Telefon:</span> <span className="text-[var(--text)]">{lead.telefon || '—'}</span> {lead.telefonValidat && <span className="text-emerald-400" title="Validat">✓</span>}</div>
          <div><span className="font-semibold text-[var(--text-muted)]">Sursă:</span> <span className="text-[var(--text)]">{lead.landingPage?.titlu || '—'}</span></div>
          <div><span className="font-semibold text-[var(--text-muted)]">Agent:</span> <span className="text-[var(--text)]">{lead.agent ? lead.agent.numeComplet : (lead.link?.agent?.numeComplet || '—')}</span></div>
          <div><span className="font-semibold text-[var(--text-muted)]">Status:</span> <span className="text-[var(--text)]">{lead.status || 'nou'}</span></div>
          <div><span className="font-semibold text-[var(--text-muted)]">Data completare:</span> <span className="text-[var(--text)]">{lead.dataCompletare ? new Date(lead.dataCompletare).toLocaleString('ro-RO') : '—'}</span></div>
        </div>
        {hasCustomFields && (
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-white/10">
            <h4 className="text-sm font-bold mb-2 text-[var(--text)]">Câmpuri completate de client</h4>
            <div className="space-y-2">
              {Object.entries(customFieldsParsed).map(([key, value]) => (
                <div key={key} className="flex gap-2">
                  <span className="font-medium text-[var(--text-muted)] shrink-0">{key}:</span>
                  <span className="text-[var(--text)] break-words">{value != null && value !== '' ? String(value) : '—'}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="mt-6 flex justify-end">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg bg-[var(--bar-bg)] text-[var(--text)] hover:bg-gray-300 dark:hover:bg-white/15">
            Închide
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================
// DELETE LEAD MODAL
// ============================================

function DeleteLeadModal({ lead, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleDelete = async () => {
    setLoading(true);
    setError('');
    try {
      await api.delete(`/op/leads/${lead.id}`);
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.error || 'Eroare la ștergerea lead-ului');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100]">
      <div className="bg-white dark:bg-[#1a1d27] rounded-xl p-6 max-w-md w-full shadow-xl border border-gray-200 dark:border-white/10">
        <h3 className="text-lg font-bold mb-2 text-red-500 dark:text-red-400">Șterge Lead</h3>
        <p className="text-gray-600 dark:text-gray-300 mb-4">
          Ești sigur că vrei să ștergi lead-ul <strong>{lead.nume || 'Nespecificat'}</strong> ({lead.telefon})?
        </p>
        {error && (
          <div className="mb-4 bg-red-500/15 border border-red-500/30 text-red-400 px-4 py-2 rounded-lg text-sm">{error}</div>
        )}
        <div className="flex gap-3 justify-end">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg bg-gray-200 dark:bg-white/10 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-white/15" disabled={loading}>
            Anulează
          </button>
          <button type="button" onClick={handleDelete} className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50" disabled={loading}>
            {loading ? 'Se șterge...' : 'Confirmă Ștergerea'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================
// LINKS LIST
// ============================================

function LinksList() {
  const [links, setLinks] = useState([]);
  const [selectedLinks, setSelectedLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  // QR Modal State
  const [showQRModal, setShowQRModal] = useState(false);
  const [selectedQRLink, setSelectedQRLink] = useState(null);

  // Bulk Send Channels
  const [bulkChannels, setBulkChannels] = useState({
    sms: false,
    whatsapp: false,
    email: false
  });

  useEffect(() => {
    fetchLinks();
  }, []);

  const fetchLinks = async () => {
    try {
      setLoading(true);
      const response = await api.get('/op/links');
      setLinks(response.data.links || []);
      setSelectedLinks([]); // Reset selection on refresh
    } catch (error) {
      console.error('Eroare încărcare link-uri:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Link copiat în clipboard!');
  };

  const handleDeleteLink = async (id) => {
    if (!window.confirm('Ești sigur că vrei să ștergi acest link?')) return;

    try {
      await api.delete(`/op/links/${id}`);
      setLinks(prev => prev.filter(l => l.id !== id));
      setSelectedLinks(prev => prev.filter(lid => lid !== id));
    } catch (error) {
      console.error('Eroare la ștergerea link-ului:', error);
      alert('Eroare la ștergere');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedLinks.length === 0) return;
    if (!window.confirm(`Sigur vrei să ștergi ${selectedLinks.length} link-uri?`)) return;

    try {
      await api.post('/op/links/bulk-delete', { ids: selectedLinks });
      setLinks(prev => prev.filter(l => !selectedLinks.includes(l.id)));
      setSelectedLinks([]);
      alert('Link-urile au fost șterse.');
    } catch (error) {
      console.error('Eroare la ștergerea multiplă:', error);
      alert('Eroare la ștergerea multiplă.');
    }
  };

  const handleBulkSend = async () => {
    if (selectedLinks.length === 0) return;

    // Check if at least one channel is selected
    const channels = Object.keys(bulkChannels).filter(k => bulkChannels[k]);
    if (channels.length === 0) {
      alert('Selectează cel puțin un canal de comunicare (SMS, WhatsApp, Email).');
      return;
    }

    if (!window.confirm(`Vrei să trimiți notificări pentru ${selectedLinks.length} link-uri prin ${channels.join(', ')}?`)) return;

    try {
      const response = await api.post('/op/links/bulk-send', {
        ids: selectedLinks,
        channels: channels
      });
      alert(response.data.message || 'Notificări trimise cu succes!');
    } catch (error) {
      console.error('Eroare la trimiterea notificărilor:', error);
      alert('Eroare la trimitere.');
    }
  };

  const toggleSelectAll = () => {
    if (selectedLinks.length === links.length) {
      setSelectedLinks([]);
    } else {
      setSelectedLinks(links.map(l => l.id));
    }
  };

  const toggleSelectLink = (id) => {
    if (selectedLinks.includes(id)) {
      setSelectedLinks(prev => prev.filter(lid => lid !== id));
    } else {
      setSelectedLinks(prev => [...prev, id]);
    }
  };

  // Helper handling single send actions
  const handleSingleSend = async (channel, agent, link) => {
    if (!agent) return;

    if (channel === 'whatsapp') {
      const message = `Salut ${agent.numeComplet}, link-ul tau de promovare este: ${link.fullUrl}`;
      if (!agent.telefon) { alert('Lipsă telefon agent'); return; }
      let phone = agent.telefon.replace(/\D/g, '');
      if (phone.length === 10 && phone.startsWith('0')) phone = '4' + phone;
      const url = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
      window.open(url, '_blank');
      return;
    }

    // Backend send for Email & SMS
    try {
      if (channel === 'email' && !agent.email) { alert('Lipsă email agent'); return; }
      if (channel === 'sms' && !agent.telefon) { alert('Lipsă telefon agent'); return; }

      const response = await api.post(`/op/links/${link.id}/send`, { channel });
      alert(response.data.message || `Notificare trimisă prin ${channel}!`);
    } catch (error) {
      console.error('Eroare trimitere:', error);
      alert(error.response?.data?.error || 'Eroare la trimiterea notificării');
    }
  };

  const handleOpenQR = (link) => {
    setSelectedQRLink(link);
    setShowQRModal(true);
  };

  const topActions = (
    <button
      type="button"
      onClick={() => setShowModal(true)}
      className="bg-blue-600 text-white rounded-md px-4 py-2 font-medium hover:bg-blue-700 transition-colors flex items-center gap-2"
    >
      <span>+</span> Generează Link Nou
    </button>
  );

  if (loading) return (
    <PremiumLayout title="Link-uri Generate" subtitle="Istoricul link-urilor pentru agenți" variant="op" topActions={null}>
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-blue" />
      </div>
    </PremiumLayout>
  );

  return (
    <PremiumLayout title="Link-uri Generate" subtitle="Istoricul link-urilor pentru agenți" variant="op" topActions={topActions}>
    <div className="space-y-6">
      <div className="section-header" style={{ marginBottom: 0 }}>
        <span className="section-title">Link-uri Generate</span>
        <span className="section-line" />
      </div>

      {selectedLinks.length > 0 && (
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mb-4 flex flex-col sm:flex-row items-center justify-between gap-4 shadow-sm">
          <div className="flex items-center gap-3">
            <span className="bg-blue-600 text-white text-xs font-bold px-2 py-1 rounded">
              {selectedLinks.length}
            </span>
            <span className="text-blue-400 font-medium text-sm">selectate</span>
          </div>

          <div className="flex items-center gap-6">
            <div className="flex items-center gap-4 bg-[var(--surface)] px-3 py-1.5 rounded border border-[var(--border)]">
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={bulkChannels.sms}
                  onChange={e => setBulkChannels({ ...bulkChannels, sms: e.target.checked })}
                  className="rounded text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">SMS</span>
              </label>
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={bulkChannels.whatsapp}
                  onChange={e => setBulkChannels({ ...bulkChannels, whatsapp: e.target.checked })}
                  className="rounded text-green-600 focus:ring-green-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">WhatsApp</span>
              </label>
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={bulkChannels.email}
                  onChange={e => setBulkChannels({ ...bulkChannels, email: e.target.checked })}
                  className="rounded text-indigo-600 focus:ring-indigo-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Email</span>
              </label>
            </div>

            <button
              onClick={handleBulkSend}
              className="bg-brand-blue text-white hover:bg-brand-blue-hover px-4 py-1.5 rounded text-sm font-medium shadow-sm transition-colors"
            >
              Trimite ({Object.values(bulkChannels).filter(Boolean).length})
            </button>

            <div className="h-6 w-px bg-gray-300 dark:bg-slate-600 mx-2"></div>

            <button
              onClick={handleBulkDelete}
              className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 font-medium text-sm flex items-center gap-1 hover:bg-gray-100 dark:hover:bg-slate-700 px-2 py-1 rounded transition-colors"
            >
              <span>🗑️</span> Șterge
            </button>
          </div>
        </div>
      )}

      {links.length === 0 ? (
        <div className="agent-leads-table-wrap p-12 text-center">
          <div className="text-6xl mb-4 opacity-50">🔗</div>
          <p className="text-[var(--text)] font-medium mb-2">Nu există link-uri generate.</p>
          <button
            onClick={() => setShowModal(true)}
            className="mt-4 px-4 py-2 rounded-lg font-medium bg-[var(--accent-glow)] text-[#8ba4fc] border border-[rgba(79,110,247,0.2)] hover:opacity-90"
          >
            Generează primul link
          </button>
        </div>
      ) : (
        <div className="agent-leads-table-wrap overflow-hidden">
          <div className="overflow-x-auto">
            <table className="agent-leads-table">
              <thead>
                <tr>
                  <th className="w-12">
                    <input
                      type="checkbox"
                      checked={links.length > 0 && selectedLinks.length === links.length}
                      onChange={toggleSelectAll}
                      className="rounded border-gray-300 text-brand-blue focus:ring-brand-blue h-4 w-4"
                    />
                  </th>
                  <th>Agent</th>
                  <th>Landing Page</th>
                  <th>URL Personalizat</th>
                  <th className="text-center">Cod QR</th>
                  <th className="text-center">Trimite</th>
                  <th>Acțiuni</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-transparent divide-y divide-gray-200 dark:divide-slate-700">
                {links.map((link) => (
                  <tr key={link.id} className={`${selectedLinks.includes(link.id) ? 'bg-blue-500/10' : ''} hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors`}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedLinks.includes(link.id)}
                        onChange={() => toggleSelectLink(link.id)}
                        className="rounded border-gray-300 text-brand-blue focus:ring-brand-blue h-4 w-4"
                      />
                    </td>
                    <td className="font-medium text-[var(--text)]">
                      {link.agent?.numeComplet}
                    </td>
                    <td className="text-[var(--text-muted)]">
                      {link.landingPage?.titlu}
                    </td>
                    <td>
                      <code className="text-xs bg-[var(--surface2)] px-2 py-1 rounded text-[#8ba4fc] font-mono truncate max-w-[200px] border border-[var(--border)]">
                        {link.fullUrl}
                      </code>
                    </td>
                    <td className="text-center">
                      <button
                        type="button"
                        onClick={() => handleOpenQR(link)}
                        className="inline-flex items-center px-3 py-1.5 border border-[var(--border)] text-xs font-medium rounded text-[var(--text)] bg-[var(--surface2)] hover:bg-[var(--bar-bg)]"
                      >
                        Generează QR
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="flex justify-center gap-2">
                        <button
                          onClick={() => handleSingleSend('sms', link.agent, link)}
                          className="px-2 py-1 text-xs font-medium rounded border border-blue-200 text-blue-600 hover:bg-blue-50 transition-colors"
                          title="Trimite SMS"
                        >
                          SMS
                        </button>
                        <button
                          onClick={() => handleSingleSend('whatsapp', link.agent, link)}
                          className="px-2 py-1 text-xs font-medium rounded border border-green-200 text-green-600 hover:bg-green-50 transition-colors"
                          title="Trimite WhatsApp"
                        >
                          WhatsApp
                        </button>
                        <button
                          onClick={() => handleSingleSend('email', link.agent, link)}
                          className="px-2 py-1 text-xs font-medium rounded border border-indigo-200 text-indigo-600 hover:bg-indigo-50 transition-colors"
                          title="Trimite Email"
                        >
                          Email
                        </button>
                      </div>
                    </td>
                    <td>
                      <div className="flex space-x-2 items-center">
                        <span className="text-xs font-bold text-[var(--text)] mr-2">{link._count?.leads || 0} leads</span>
                        <button
                          type="button"
                          onClick={() => copyToClipboard(link.fullUrl)}
                          className="text-[var(--text-muted)] hover:text-[#8ba4fc] px-2 py-1 rounded hover:bg-[var(--bar-bg)] text-xs font-medium border border-[var(--border)]"
                          title="Copiază Link"
                        >
                          Copiază
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteLink(link.id)}
                          className="text-red-400 hover:bg-red-500/15 px-2 py-1 rounded text-xs font-medium border border-red-500/30"
                          title="Șterge Link"
                        >
                          Șterge
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showModal && <GenerateLinkModal onClose={() => setShowModal(false)} onSuccess={fetchLinks} />}

      {showQRModal && (
        <QRModal
          link={selectedQRLink}
          onClose={() => { setShowQRModal(false); setSelectedQRLink(null); }}
        />
      )}
    </div>
    </PremiumLayout>
  );
}

function GenerateLinkModal({ onClose, onSuccess }) {
  const [agents, setAgents] = useState([]);
  const [pages, setPages] = useState([]);
  const [formData, setFormData] = useState({
    agentId: [],
    landingPageId: '',
    dataExpirare: '',
    trimiteEmail: true,
    trimiteSMS: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [agentsRes, pagesRes] = await Promise.all([
          api.get('/op/agenti'),
          api.get('/op/landing-pages')
        ]);
        setAgents(agentsRes.data.agents || []);
        setPages(pagesRes.data.pages || []);
      } catch (err) {
        console.error('Error fetching data:', err);
      }
    };
    fetchData();
  }, []);

  const handleAgentChange = (e) => {
    const options = e.target.options;
    const value = [];
    for (let i = 0, l = options.length; i < l; i++) {
      if (options[i].selected) {
        value.push(options[i].value);
      }
    }
    setFormData({ ...formData, agentId: value });
  };

  const handleSelectAllAgents = () => {
    const allAgentIds = agents.map(a => a.id.toString());
    setFormData({ ...formData, agentId: allAgentIds });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await api.post('/op/links/generate', formData);
      onSuccess();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Eroare la generarea link-ului');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-bold mb-4">Generează Link-uri (Bulk)</h3>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <div className="flex justify-between items-center mb-1">
              <label className="block text-sm font-medium text-gray-700">
                Selectează Agenți (Multi-select)
              </label>
              <button
                type="button"
                onClick={handleSelectAllAgents}
                className="text-xs text-blue-600 hover:text-blue-800"
              >
                Selectează Toți
              </button>
            </div>
            <select
              multiple
              required
              value={formData.agentId}
              onChange={handleAgentChange}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 h-32"
            >
              {agents.map(agent => (
                <option key={agent.id} value={agent.id}>
                  {agent.numeComplet} ({agent.telefon})
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Ține apăsat Ctrl (sau Cmd) pentru a selecta mai mulți agenți.
            </p>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Selectează Landing Page
            </label>
            <select
              required
              value={formData.landingPageId}
              onChange={(e) => setFormData({ ...formData, landingPageId: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            >
              <option value="">-- Alege o pagină --</option>
              {pages.map(page => (
                <option key={page.id} value={page.id}>
                  {page.titlu} (/lp/{page.slug})
                </option>
              ))}
            </select>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Data Expirare Link-uri (Opțional)
            </label>
            <input
              type="date"
              value={formData.dataExpirare}
              onChange={(e) => setFormData({ ...formData, dataExpirare: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            />
          </div>

          <div className="mb-6 space-y-2 bg-gray-50 p-3 rounded-lg border border-gray-200">
            <h4 className="text-sm font-medium text-gray-900">Notificări Agent</h4>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.trimiteEmail}
                onChange={(e) => setFormData({ ...formData, trimiteEmail: e.target.checked })}
                className="mr-2 h-4 w-4 text-blue-600 rounded border-gray-300"
              />
              <span className="text-sm text-gray-700">Trimite Email la Agent</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.trimiteSMS}
                onChange={(e) => setFormData({ ...formData, trimiteSMS: e.target.checked })}
                className="mr-2 h-4 w-4 text-blue-600 rounded border-gray-300"
              />
              <span className="text-sm text-gray-700">Trimite SMS la Agent</span>
            </label>
          </div>

          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-600 px-4 py-2 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300"
            >
              Anulează
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Se generează...' : `Generează pt ${formData.agentId.length} Agenți`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


// End of file
